/**
 * CL-STORAGE-001 — Secure drawing Storage binding (versioned paths).
 *
 * Target:
 *   pmProjectFiles/{projectId}/files/{fileId}/original
 *   pmProjectFiles/{projectId}/files/{fileId}/thumbnail
 *
 * Authorization is never derived from filename or displayTitle.
 * New drawing uploads must not persist tokenized download URLs.
 */

import {
  buildVersionedPmProjectFileObjectPath,
  fetchAuthorizedProjectFileBlob,
  revokeTemporaryObjectUrl,
  STORAGE_ORIGINAL_BLOB_FETCH_TIMEOUT_MS,
} from "./projectFileAuthenticatedBlob";
import {
  STORAGE_AUDIENCES,
  STORAGE_AUTH_DOMAINS,
  buildVersionedStorageAuthorizationFields,
} from "./projectFileStorageAuthorizationModel";
import { buildStorageAdminAuthUids } from "./storageViewAuthProjection";

function safeStr(value) {
  return String(value ?? "").trim();
}

export const PROJECT_FILE_STORAGE_SCHEMA_VERSION = 2;

/** Schema version written on new/replaced main drawing objects. */
export const SECURE_DRAWING_STORAGE_SCHEMA_VERSION = PROJECT_FILE_STORAGE_SCHEMA_VERSION;

export function isVersionedPmProjectFileStoragePath(storagePath) {
  return /^pmProjectFiles\/[^/]+\/files\/[^/]+\/(original|thumbnail)$/.test(safeStr(storagePath));
}

export function isLegacyPmProjectFileMainPath(storagePath) {
  const p = safeStr(storagePath);
  if (!p || isVersionedPmProjectFileStoragePath(p)) return false;
  if (/^pmProjectFiles\/[^/]+\/files\//.test(p)) return false;
  if (/^pmProjectFiles\/[^/]+\/(clientUploads|collaboratorRevisions|receipts|archivePhotos)\//.test(p)) {
    return false;
  }
  if (/^pmProjectFiles\/[^/]+\/__thumbnails\//.test(p)) return false;
  return /^pmProjectFiles\/[^/]+\//.test(p);
}

/** Explicit Storage object paths on pmProjectFiles (mirrors server read-path model). */
export function isExplicitPmProjectFileStoragePath(storagePath) {
  const p = safeStr(storagePath);
  if (!p || !p.startsWith("pmProjectFiles/")) return false;
  if (isVersionedPmProjectFileStoragePath(p)) return true;
  if (/^pmProjectFiles\/[^/]+\/(clientUploads|collaboratorRevisions|receipts|archivePhotos)\//.test(p)) {
    return true;
  }
  if (/^pmProjectFiles\/[^/]+\/__thumbnails\//.test(p)) return true;
  return isLegacyPmProjectFileMainPath(p);
}

export function buildVersionedDrawingOriginalPath(projectId, fileId) {
  return buildVersionedPmProjectFileObjectPath(projectId, fileId, "original");
}

export function buildVersionedDrawingThumbnailPath(projectId, fileId) {
  return buildVersionedPmProjectFileObjectPath(projectId, fileId, "thumbnail");
}

/**
 * Whether this upload should use the secure versioned main-file path.
 * Drawings only — photos/docs/receipts stay on their existing conventions.
 */
export function shouldUseVersionedDrawingStorage({
  libraryType = "",
  category = "",
  projectFileDomain = "",
  uploadContext = "",
} = {}) {
  const lt = safeStr(libraryType).toLowerCase();
  const cat = safeStr(category).toLowerCase();
  const domain = safeStr(projectFileDomain).toLowerCase();
  const ctx = safeStr(uploadContext).toLowerCase();
  if (lt === "drawing" || cat === "drawings" || domain === "drawing") return true;
  if (ctx === "drawing" || ctx === "drawings") return true;
  return false;
}

/**
 * Fields persisted on pmProjectFiles for a secure main drawing object.
 * Intentionally omits tokenized url/thumbnailUrl for new writes.
 */
export function buildSecureDrawingStorageWriteFields({
  projectId,
  fileId,
  includeThumbnailPath = false,
  actorUid = "",
  existingFile = null,
  preserveViewAuthUids = false,
} = {}) {
  const pid = safeStr(projectId);
  const fid = safeStr(fileId);
  const storagePath = buildVersionedDrawingOriginalPath(pid, fid);
  if (!storagePath) return null;
  const fields = {
    storagePath,
    path: storagePath,
    storageSchemaVersion: SECURE_DRAWING_STORAGE_SCHEMA_VERSION,
    // Do not persist new download tokens for secure drawings.
    url: null,
  };
  if (includeThumbnailPath) {
    const thumb = buildVersionedDrawingThumbnailPath(pid, fid);
    fields.thumbnailStoragePath = thumb || null;
    fields.thumbnailUrl = null;
  }
  const actor = safeStr(actorUid);
  if (actor) {
    fields.storageAdminAuthUids = buildStorageAdminAuthUids({
      file: existingFile,
      actorUid: actor,
    });
  }
  if (!existingFile && !preserveViewAuthUids) {
    fields.storageViewAuthUids = [];
  }
  const authz = buildVersionedStorageAuthorizationFields({
    storageAuthDomain: STORAGE_AUTH_DOMAINS.DRAWING,
    storageAudience: STORAGE_AUDIENCES.SELECTED_USERS,
  });
  if (authz) {
    fields.storageAuthDomain = authz.storageAuthDomain;
    fields.storageAudience = authz.storageAudience;
  }
  return fields;
}

/** True when the file record is bound to the secure schema (or already on versioned path). */
export function projectFileUsesSecureStorage(file = {}) {
  const schema = Number(file?.storageSchemaVersion || 0);
  if (schema >= SECURE_DRAWING_STORAGE_SCHEMA_VERSION) return true;
  const path = safeStr(file?.storagePath || file?.path || "");
  return isVersionedPmProjectFileStoragePath(path);
}

/**
 * Resolve the Storage object path for authenticated reads.
 * Prefer explicit secure binding; never invent entitlement from displayTitle/name.
 */
export function resolveProjectFileSecureObjectPath(file = {}, { kind = "original" } = {}) {
  const objectKind = safeStr(kind) === "thumbnail" ? "thumbnail" : "original";
  const pid = safeStr(file?.projectId);
  const fid = safeStr(file?.id || file?.fileId);
  if (objectKind === "thumbnail") {
    const thumb = safeStr(file?.thumbnailStoragePath || file?.thumbnailPath || "");
    if (thumb && isExplicitPmProjectFileStoragePath(thumb)) return thumb;
    const original = resolveProjectFileSecureObjectPath(file, { kind: "original" });
    if (isVersionedPmProjectFileStoragePath(original) && pid && fid) {
      return buildVersionedDrawingThumbnailPath(pid, fid);
    }
    return "";
  }
  const direct = safeStr(file?.storagePath || file?.storageOriginalPath || "");
  if (direct && isExplicitPmProjectFileStoragePath(direct)) return direct;
  const fromPath = safeStr(file?.path || file?.fullPath || "");
  if (fromPath && isExplicitPmProjectFileStoragePath(fromPath)) return fromPath;
  if (safeStr(file?.storageAuthDomain) === "procurement_order_attachment" && pid && fid) {
    return buildVersionedDrawingOriginalPath(pid, fid);
  }
  if (projectFileUsesSecureStorage(file) && pid && fid) {
    return buildVersionedDrawingOriginalPath(pid, fid);
  }
  return "";
}

/**
 * Isolated legacy compatibility: only for pre-migration records that still have
 * a stored tokenized URL or legacy path. Must never expand entitlement.
 */
export function projectFileAllowsLegacyTokenizedUrlCompat(file = {}) {
  if (projectFileUsesSecureStorage(file)) return false;
  const url = safeStr(file?.url);
  if (url && /^https?:/i.test(url)) return true;
  const path = safeStr(file?.storagePath || file?.path || "");
  return isLegacyPmProjectFileMainPath(path);
}

export function listKnownLegacyTokenizedUrlConsumerSurfaces() {
  return [
    "ProjectFileOpenControls (<a href=stored url> when not secure)",
    "projectFileOpenUrl.resolveProjectFileOpenUrl method=stored_legacy_compat|refreshed_legacy_compat getDownloadURL",
    "InAppFilePreviewDrawer legacy iframe/img via file.url / thumbnailUrl (secure uses AuthenticatedFilePreviewMedia)",
    "ReviewDrawerPreviewPanel legacy sheet thumb via file.thumbnailUrl (secure uses AuthenticatedFilePreviewMedia)",
    "ClientProjectDetail getPreviewUrl / ClientFileListThumbnail",
    "ClientPortalProjectRow getPreviewUrl",
    "ClientFileMarkup canvas/image source",
    "AdminProjectDetail lightbox getPreviewUrl",
    "PermitRepositoryV2 Open file href",
    "client document / photo surfaces (out of drawing scope)",
  ];
}

/**
 * Open/download via authenticated getBlob for secure files.
 * Returns temporary object URL; caller must revoke.
 */
export async function openProjectFileViaAuthenticatedBlob(file = {}, deps = {}) {
  const path = resolveProjectFileSecureObjectPath(file, { kind: "original" });
  if (!path) {
    return { ok: false, errorCode: "missing_secure_path", message: "Secure storage path is missing." };
  }
  const result = await fetchAuthorizedProjectFileBlob({
    storage: deps.storage,
    path,
    timeoutMs:
      deps.timeoutMs === undefined || deps.timeoutMs === null
        ? STORAGE_ORIGINAL_BLOB_FETCH_TIMEOUT_MS
        : deps.timeoutMs,
    getBlobImpl: deps.getBlob,
    refImpl: deps.ref,
    createObjectURL: deps.createObjectURL,
  });
  if (!result.ok) {
    return {
      ok: false,
      errorCode: result.errorCode || "authorized_blob_failed",
      message: "Authorized file open failed.",
    };
  }
  return {
    ok: true,
    method: "authenticated_blob",
    objectUrl: result.objectUrl,
    blob: result.blob,
    path: result.path,
    fileId: safeStr(file?.id || file?.fileId),
    revoke: () => revokeTemporaryObjectUrl(result.objectUrl, deps.revokeObjectURL),
  };
}

export { buildVersionedPmProjectFileObjectPath, revokeTemporaryObjectUrl };
